/**
 * Component: Tooltip - reusable, dependency-free. Triggered by any [data-tooltip] element
 * via tooltip.js. The bubble is appended to <body>; JS centers it over the trigger and flips
 * below / shifts horizontally to stay inside the viewport.
 */
(() => {
  const TRIGGER_SELECTOR = '[data-tooltip]';
  const VIEWPORT_PADDING = 8;
  const TRIGGER_GAP = 8;
  const ARROW_INSET = 12;

  class Tooltip {
    constructor() {
      this.bubble = null;
      this.inner = null;
      this.arrow = null;
      this.trigger = null;

      this.handlePointerEnter = this.handlePointerEnter.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.reposition = this.reposition.bind(this);
    }

    init() {
      document.addEventListener('mouseover', this.handlePointerEnter);
      document.addEventListener('mouseout', this.handlePointerLeave);
      document.addEventListener('focusin', this.handlePointerEnter);
      document.addEventListener('focusout', this.handlePointerLeave);
    }

    build() {
      if (this.bubble) return;

      this.bubble = document.createElement('div');
      this.bubble.className = 'tooltip';
      this.bubble.setAttribute('aria-hidden', 'true');
      this.bubble.hidden = true;

      this.arrow = document.createElement('span');
      this.arrow.className = 'tooltip__arrow';

      this.inner = document.createElement('span');
      this.inner.className = 'tooltip__inner';

      this.bubble.append(this.arrow, this.inner);
      document.body.appendChild(this.bubble);
    }

    handlePointerEnter(event) {
      const trigger = event.target.closest(TRIGGER_SELECTOR);
      if (!trigger || trigger === this.trigger) return;

      const text = trigger.dataset.tooltip;
      if (!text) return;

      this.build();
      this.trigger = trigger;
      this.inner.textContent = text;
      this.bubble.hidden = false;
      this.reposition();

      window.addEventListener('scroll', this.reposition, true);
      window.addEventListener('resize', this.reposition);
    }

    handlePointerLeave(event) {
      if (!this.trigger) return;

      const trigger = event.target.closest(TRIGGER_SELECTOR);
      if (trigger !== this.trigger) return;

      const next = event.relatedTarget;
      if (next && trigger.contains(next)) return;

      this.hide();
    }

    hide() {
      if (this.bubble) {
        this.bubble.hidden = true;
        this.bubble.classList.remove('tooltip--bottom');
      }
      this.trigger = null;
      window.removeEventListener('scroll', this.reposition, true);
      window.removeEventListener('resize', this.reposition);
    }

    reposition() {
      if (!this.trigger || !this.bubble) return;

      const text = this.trigger.dataset.tooltip;
      if (!text) {
        this.hide();
        return;
      }
      this.inner.textContent = text;

      const triggerRect = this.trigger.getBoundingClientRect();
      const bubbleRect = this.bubble.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;

      const placeBelow = triggerRect.top - bubbleRect.height - TRIGGER_GAP < VIEWPORT_PADDING;
      this.bubble.classList.toggle('tooltip--bottom', placeBelow);

      const top = placeBelow
        ? triggerRect.bottom + TRIGGER_GAP
        : triggerRect.top - bubbleRect.height - TRIGGER_GAP;

      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const maxLeft = viewportWidth - bubbleRect.width - VIEWPORT_PADDING;
      const left = Math.max(VIEWPORT_PADDING, Math.min(triggerCenter - bubbleRect.width / 2, maxLeft));

      this.bubble.style.top = `${Math.round(top)}px`;
      this.bubble.style.left = `${Math.round(left)}px`;

      const arrowLeft = Math.min(Math.max(triggerCenter - left, ARROW_INSET), bubbleRect.width - ARROW_INSET);
      this.arrow.style.left = `${Math.round(arrowLeft)}px`;
    }
  }

  const tooltip = new Tooltip();
  tooltip.init();
})();
